import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, User, Phone } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SignInFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  onSubmit: (email: string, password: string, e: React.FormEvent) => void;
  onSignUp: () => void;
  onForgotPassword: () => void;
  validationError: string;
  setValidationError: (error: string) => void;
}

const SignInForm = ({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  onSubmit,
  onSignUp,
  onForgotPassword,
  validationError,
  setValidationError
}: SignInFormProps) => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Clear any previous validation errors
    setValidationError("");
    
    // Use phone number as email field if phone login is selected
    const loginIdentifier = loginMethod === 'phone' ? phoneNumber : email;
    onSubmit(loginIdentifier, password, e);
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format for Kenya numbers
    if (digits.startsWith('0')) {
      return `+254${digits.slice(1)}`;
    } else if (digits.startsWith('254')) {
      return `+${digits}`;
    } else if (!digits.startsWith('+')) {
      return `+254${digits}`;
    }
    return digits;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedPhone);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex justify-center mb-2">
          <User className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl">Welcome Back</CardTitle>
        <CardDescription>
          Sign in to your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={loginMethod} onValueChange={(value) => setLoginMethod(value as 'email' | 'phone')} className="mb-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone
            </TabsTrigger>
          </TabsList>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <TabsContent value="email" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="phone" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+254700000000 or 0700000000"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  required
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Use the phone number from your SMS invitation
                </p>
              </div>
            </TabsContent>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>

            {validationError && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {validationError}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </Tabs>

        <div className="mt-4 text-center space-y-2">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:underline"
            disabled={loading}
          >
            Forgot your password?
          </button>
          
          <div className="text-sm text-muted-foreground">
            Don't have an account?{" "}
            <button
              type="button"
              onClick={onSignUp}
              className="text-primary hover:underline"
              disabled={loading}
            >
              Sign up here
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SignInForm;